// src/mockData/calculations.ts

import { CUSTOMERS } from './customers';
import { RECEIPTS } from './receipts';
import { DISPUTES } from './disputes';
import { PROMISES } from './promises';

export function getTodaysPriorities(filter: string) {
  const customers = filter === 'everyone' 
    ? CUSTOMERS 
    : CUSTOMERS.filter(c => c.assignedTo === filter);
    
  const receipts = filter === 'everyone'
    ? RECEIPTS
    : RECEIPTS.filter(r => r.assignedTo === filter);
    
  const disputes = filter === 'everyone'
    ? DISPUTES
    : DISPUTES.filter(d => d.assignedTo === filter);
    
  const promises = filter === 'everyone'
    ? PROMISES
    : PROMISES.filter(p => p.assignedTo === filter);
  
  // Collection Queue: customers with high risk or over 90 days debt
  const queueCustomers = customers.filter(c => c.riskLevel === 'high' || c.over90 > 50000);
  
  // Unallocated receipts
  const unallocatedReceipts = receipts.filter(r => r.status === 'unallocated');
  
  // Promises due today and broken promises
  const promisesDueToday = promises.filter(p => p.status === 'due-today');
  const brokenPromises = promises.filter(p => p.status === 'broken');
  
  // Active disputes requiring response
  const activeDisputes = disputes.filter(d => d.status === 'requiring-response');
  
  return {
    casesDueToday: {
      count: queueCustomers.length,
      value: queueCustomers.reduce((sum, c) => sum + c.totalOutstanding, 0)
    },
    unallocatedReceipts: {
      count: unallocatedReceipts.length,
      value: unallocatedReceipts.reduce((sum, r) => sum + r.remaining, 0)
    },
    promisesDueToday: {
      count: promisesDueToday.length,
      value: promisesDueToday.reduce((sum, p) => sum + p.amount, 0),
      brokenCount: brokenPromises.length,
      brokenValue: brokenPromises.reduce((sum, p) => sum + p.amount, 0)
    },
    disputesRequiringResponse: {
      count: activeDisputes.length,
      value: activeDisputes.reduce((sum, d) => sum + d.amount, 0)
    }
  };
}

export function getAgeingPortfolio(filter: string) {
  const customers = filter === 'everyone' 
    ? CUSTOMERS 
    : CUSTOMERS.filter(c => c.assignedTo === filter);
  
  const totals = {
    current030: 0,
    over3160: 0,
    over6190: 0,
    over91120: 0,
    over121180: 0,
    over180: 0,
    over365: 0,
    total: 0
  };
  
  customers.forEach(c => {
    totals.current030 += c.current030;
    totals.over3160 += c.over3160;
    totals.over6190 += c.over6190;
    totals.over91120 += c.over91120;
    totals.over121180 += c.over121180;
    totals.over180 += c.over180;
    totals.over365 += c.over365;
    totals.total += c.totalOutstanding;
  });
  
  return totals;
}

export function getDisputeMetrics(filter: string) {
  const disputes = filter === 'everyone'
    ? DISPUTES
    : DISPUTES.filter(d => d.assignedTo === filter);
    
  const activeDisputes = disputes.filter(d => d.status === 'requiring-response');
  
  return {
    activeCount: activeDisputes.length,
    totalAmount: activeDisputes.reduce((sum, d) => sum + d.amount, 0),
    avgResolutionTime: 14.3,
    resolutionRate: 87.5,
    byPriority: {
      critical: activeDisputes.filter(d => d.priority === 'critical').length,
      high: activeDisputes.filter(d => d.priority === 'high').length,
      medium: activeDisputes.filter(d => d.priority === 'medium').length,
      low: activeDisputes.filter(d => d.priority === 'low').length
    }
  };
}

export function getCollectionMetrics(filter: string) {
  const customers = filter === 'everyone' 
    ? CUSTOMERS 
    : CUSTOMERS.filter(c => c.assignedTo === filter);
  
  const totalDebt = customers.reduce((sum, c) => sum + c.totalOutstanding, 0);
  const collectedAmount = totalDebt * (filter === 'user-001' ? 0.48 : 0.785); // Sarah: 48%, Team: 78.5%
  
  return {
    totalDebt,
    collectedAmount,
    collectionRate: filter === 'user-001' ? 48 : 78.5,
    targetRate: 85,
    variance: (filter === 'user-001' ? 48 : 78.5) - 85
  };
}

export function getTodaysWins(filter: string) {
  // Mock data for today's wins
  const baseWins = {
    paymentsReceived: filter === 'user-001' ? 3 : 18,
    paymentsAmount: filter === 'user-001' ? 45000 : 285000,
    promisesKept: filter === 'user-001' ? 2 : 12,
    promisesAmount: filter === 'user-001' ? 28000 : 175000,
    disputesResolved: filter === 'user-001' ? 1 : 8,
    disputesAmount: filter === 'user-001' ? 15000 : 95000
  };
  
  return baseWins;
}

export function getKeyMetrics(filter: string) {
  const customers = filter === 'everyone' 
    ? CUSTOMERS 
    : CUSTOMERS.filter(c => c.assignedTo === filter);
  
  const totalDebt = customers.reduce((sum, c) => sum + c.totalOutstanding, 0);
  const over90Debt = customers.reduce((sum, c) => sum + c.over90, 0);
  const over180Debt = customers.reduce((sum, c) => sum + c.over180, 0);
  
  return {
    totalOutstanding: totalDebt,
    over90Days: over90Debt,
    over180Days: over180Debt,
    dso: filter === 'user-001' ? 52 : 45, // Days Sales Outstanding
    collectionEfficiency: filter === 'user-001' ? 48 : 78.5,
    averageDebtAge: filter === 'user-001' ? 68 : 52,
    customerCount: customers.length
  };
}
